import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { useContext, useState } from "react";
import { FragmentDefiner } from "../../../GlobalHooks/useReadFragment";
import { monthLongName } from "@Utils/dateFormats";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import { getStratumText } from "src/utils/Functions";
import { TechnicalSheetType } from "shared-components";
import { REGISTER_MUTATION, UPDATE_USER_MUTATION, USER_EXISTS_QUERY } from "shared-components/Components/User/User.querys";
import { useUser } from "shared-components/Components/User/User.hook";
import { technicalSheetCountries } from "shared-components/ViewFragments/SearchResults/Sider/TopBarFilters/options";
import { trackNpsLeadSent } from "shared-components/Components/NpsSurveyCard/npsSurvey.service";

export const FRAGMENT_PROJECT_INFO = new FragmentDefiner(
	"Property",
	`
    project {
		id
        percentage_sold
        percentage_finished
		ocupation
		finalization
		builder
		constructionState{
			name
		}
    }
`
);

const pojectInfoTitles = Object.freeze({
	percentageSold: "Porcentaje vendido",
	percentageFinished: "Porcentaje terminado",
	ocupation: "Ocupación",
	builder: "Desarrollador",
	finalization: "Fecha de Finalización",
	constructionState: "Estado del Proyecto",
	financing: "Financiación",
});

export type ProjectInfoType = {
	percentageSold: number | null;
	percentageFinished: number | null;
	ocupation: Date | null;
	builder: string;
	finalization: string;
	constructionState: string;
	stratum?: number;
	garages?: number;
	technicalSheet?: TechnicalSheetType[]; //la Technical 
};

export type ProjectInfoItem = {
	text: string;
	value: string;
	field: string;
	show: boolean
};

export interface useProjectInfoResponse {
	projectInfoList: ProjectInfoItem[];
	sendLead: (field: string, extraFields?: any) => Promise<any>;
	registerMutation: any;
	updateUser: any;
	userExistsCustomQuery: any;
	dataRegisterMutation: any;
	formCompleted: Boolean;
}

interface ContactInput {
	property_id: string;
	source: number;
	type: string;
	financing_information: boolean;
	message?: string;
	visit_type?: string;
	date?: string;
	utm_args?: UtmArgs;
	name?: string;
	email?: string;
	phone?: string
	isAutoGenerated?: boolean;
}

interface UtmArgs {
	utm_content?: string | string[];
	utm_source?: string | string[];
	utm_medium?: string | string[];
	utm_campaign?: string | string[];
}

const SEND_LEAD_MUTATION = gql`
  mutation sendLead($input: LeadInput!) {
    sendLead(input: $input) {
      id
    }
  }
`;

export const useProjectInfo = (
	id: string,
	projectInfo: ProjectInfoType,
	utm = null
): useProjectInfoResponse => {
	const { user, isLoggedIn } = useUser();
	const [formCompleted, setFormCompleted] = useState(false);
	const [sendLeadMutation] = useMutation(SEND_LEAD_MUTATION, {
		onCompleted: () => setFormCompleted(true),
	});
	const { country_code } = useContext(ConfigStateContext);

	const [
		userExistsCustomQuery,
		{ data: dataUserExistCustom, loading: userExistsCustomLoading },
	] = useLazyQuery(USER_EXISTS_QUERY, {
		fetchPolicy: "network-only",
	});

	const [
		registerMutation,
		{ loading: registerLoading, data: dataRegisterMutation },
	] = useMutation(REGISTER_MUTATION);

	const [updateUserMutation, { loading: updateLoading }] = useMutation(UPDATE_USER_MUTATION);

	const updateUser = ({ name, phone }) => {
		return updateUserMutation({ variables: { name, phone } });
	};

	const sendLeadTracked = async (variables) => {
		const response = await sendLeadMutation(variables);
		if (response?.data?.sendLead?.id) trackNpsLeadSent();
		return response;
	};

	const sendLead = async (field: string, extraFields = undefined) => {
		let input: ContactInput = {
			type: "PROJECT",
			property_id: id,
			message: field,
			source: 14,
			isAutoGenerated: true,
			financing_information: false,
		};

		if (utm && (utm.utm_campaign || utm.utm_content || utm.utm_medium || utm.utm_source)) {
			input.utm_args = {};
			if (utm.utm_campaign) {
				input.utm_args.utm_campaign = Array.isArray(utm.utm_campaign) ? utm.utm_campaign[0] : utm.utm_campaign.toString();
			}
			if (utm.utm_content) {
				input.utm_args.utm_content = Array.isArray(utm.utm_content) ? utm.utm_content[0] : utm.utm_content.toString();
			}
			if (utm.utm_medium) {
				input.utm_args.utm_medium = Array.isArray(utm.utm_medium) ? utm.utm_medium[0] : utm.utm_medium.toString();
			}
			if (utm.utm_source) {
				input.utm_args.utm_source = Array.isArray(utm.utm_source) ? utm.utm_source[0] : utm.utm_source.toString();
			}
		}

		if (extraFields) {
			input.name = extraFields.name;
			input.email = extraFields.email;
			input.phone = extraFields.phone;
		}

		if (!isLoggedIn) {
			return userExistsCustomQuery({ variables: { email: input.email } }).then(data => {
				if (data?.error?.message?.includes("no existe")) {
					return registerMutation({
						variables: {
							email: input.email,
							name: input.name,
						},
					})
						.then(({ data }) => {
							return updateUser({
								name: input.name,
								phone: input.phone,
							}).then(() => {
								user.refetch();
								return sendLeadTracked({
									variables: {
										input,
									},
								}).then(data => {
									user.refetch();
									return data;
								});
							});
						})
						.catch(error => {
							console.error(error);
						});
				} else {
					return sendLeadTracked({
						variables: {
							input,
						},
					});
				}
			});
		}

		return sendLeadTracked({
			variables: {
				input,
			},
		});
	};

	const formatData = (key: string, value: any): string => {
		if (!value) return null;

		switch (key) {
			case "percentageSold":
				return `${value}%`;
			case "percentageFinished":
				return `${value}%`;
			case "ocupation":
				const date = new Date(value);
				return `${monthLongName(value)} ${date.getUTCFullYear()}`;
			default:
				return value;
		}
	};

	const showItem = (key) => {
		const item = technicalSheetCountries.filter((item) => item.field === key)[0]

		if (item && item.countries.includes(country_code)) return true;
		else return false
	}

	const makeProjectInfoList = (info: ProjectInfoType): ProjectInfoItem[] => {
		let data = Object.entries(info).filter(([key]) => key !== "technicalSheet" && key !== "finalization").map(([key, value]) => {
			const text = pojectInfoTitles[key];
			const formattedValue = key === "stratum" ? getStratumText(value) : formatData(key, value);
			const show = showItem(key);

			return {
				field: key,
				value: formattedValue,
				text,
				show
			};
		});

		if (info.technicalSheet) {
			const itemsByField = new Map<string, ProjectInfoItem>(
				data.map(item => [item.field, item])
			);

			info.technicalSheet.forEach(item => {
				const existing = itemsByField.get(item.field);

				itemsByField.set(item.field, {
					...(existing ?? {}),
					field: item.field,
					text: item.text ?? existing?.text ?? "",
					value: item.value ?? existing?.value ?? "",
					show: existing?.show ?? true,
				});
			});

			data = Array.from(itemsByField.values());
		}

		return data
	};

	return {
		projectInfoList: makeProjectInfoList(projectInfo),
		sendLead,
		formCompleted,
		// TODO: remove it when the branch is merged
		registerMutation,
		updateUser,
		userExistsCustomQuery,
		dataRegisterMutation
	};
};
